import type { CSSProperties } from 'react'
import { BUNDLED_PACK, BY_SLUG, PATHS, RENAMED } from './categoryIconPaths'
import { CatalogueIconImg } from './CatalogueIconImg'

/**
 * The category's own glyph — Task 13, the only per-category mark on a
 * category row (#454 removed category colour, and the coloured dot with it).
 * The design project shipped a purpose-built 88-glyph taxonomy (`categoryIconPaths.ts`, treatment 4a — full
 * taxonomy, filled silhouettes, no container, 24x24 grid, `fill-rule:
 * evenodd`). Since backend #401 that pack is the FALLBACK and the server
 * supplies the icon by URL — see below.
 *
 * **One class, for one thing a token has to reach.** The bundled `<svg>`
 * fallback sets every visual property inline, the same as `Icon.tsx`, the
 * sibling in this directory — there is nothing on it that needs a hover
 * state (the glyph is decorative, see below), so an inline `style` is not
 * the thing rule 2 warns about. The catalogue `<img>` is different: its
 * theme inversion (#445, below) has to come from a token-backed rule, which
 * an inline style cannot carry, so `CatalogueIconImg` draws it with the
 * shared class (`catalogueIconImg.css`, #467). Sizing follows the inline pattern regardless: a
 * `size` prop rather than a CSS rule, the same shape `Icon.tsx` already uses
 * for its own `size`.
 *
 * ## Colour — Ruling N, reversed
 *
 * **The glyph carries no colour of its own.** It fills with `currentColor`,
 * so whatever mounts it decides the ink: `.categories__glyph` dims it to
 * `--ink2` on a category row, `.icon-grid__glyph` gives it `--control-ink`
 * inside the picker.
 *
 * Ruling N originally said the opposite — fill with the category's own colour,
 * through a `--swatch` custom property. The design settles it the other way:
 * every glyph is `color-mix(in oklab, var(--ink) 62%, transparent)`, uniform
 * down the column. (The design also kept a colour dot on SUBCATEGORY rows;
 * #454 removed category colour from the web app entirely, so no row of any
 * kind carries one now.) At 88 rows that is the better call — saturated glyphs compete with the row's own
 * text and, later, with its status and rule chips, which are the signal worth
 * scanning for. `--ink2` is that 62% already (`tokens.css:526` defines the
 * dark rung as `rgba(252,252,250,.62)` exactly), so the treatment arrives
 * through a token rather than a `color-mix` this file would have to name.
 *
 * `fill: currentColor`, not SVG's initial `fill` — the initial value is black,
 * not `currentColor`, so a glyph with no `fill` at all would paint black on
 * both themes rather than inheriting the ink around it.
 *
 * ## Where the icon comes from — `icon_url` first (#447)
 *
 * **The server owns the icon now.** Backend #401 replaced `icon_paths`,
 * `web_icon` and the pack metadata with two fields: `icon`, a slug from the
 * 1,518-icon Phosphor duotone catalogue, and `icon_url`, an absolute URL to
 * that icon's SVG. `icon_url` is **null** when the icon is null, a legacy
 * value, or not in the catalogue.
 *
 * So the order is: **`icon_url`, then the bundled pack, then
 * `Uncategorized`.** The bundled pack is what a row with no URL falls back
 * to, not the source.
 *
 * **A plain `<img>`, untinted, by the user's decision.** The catalogue SVGs
 * are `fill="currentColor"`, and an `<img>` gives them no current colour to
 * inherit, so they paint black regardless of theme. Tinting would need a CSS
 * `mask-image` or an inlined SVG, and both need CORS from the icons host; the
 * user chose not to colour icons at all, so neither is built. The `<img>`
 * needs no CORS. The `currentColor` treatment below applies ONLY to the
 * bundled fallback glyph.
 *
 * **Black is invisible on a dark row — #445.** `CatalogueIconImg` gives the
 * `<img>` the class `catalogue-icon-img` (`catalogueIconImg.css`), which reads
 * `filter: var(--catalogue-icon-filter)` — `none` in light, `invert(1)` in
 * dark (`tokens.css`), so the untinted black glyph becomes white where it
 * would otherwise vanish. Still not a colour: `invert()` is a transform, not
 * a hue, so this is not the reversal the paragraph above rules out. The
 * fallback `<svg>` below carries no such class — it is already correct in
 * both themes and inverting it would flip its `--ink2` tint.
 *
 * **A failed load falls back too.** A 404 or a dropped connection on the
 * icons host would otherwise leave a broken-image box in the row. The failed
 * URL is remembered rather than a boolean, so a category whose `icon_url`
 * changes (a refetch after an edit) gets a fresh attempt.
 *
 * ## The fallback chain — Ruling O, and what survives of it
 *
 * Reached only when there is no usable `icon_url`.
 *
 * `icon` → `RENAMED` alias → exact `name` → `Uncategorized`:
 *
 *   - `icon` catches a category saved with one of the 88 bundled slugs by the
 *     picker that predated the catalogue. A catalogue slug
 *     (`guitar-duotone`) is not in `BY_SLUG` and falls straight through.
 *   - `name` resolves the system categories whose `icon_url` is null.
 *   - `Uncategorized` catches everything else.
 *
 * `!= null`, never `!== null`: `icon` and `icon_url` are
 * `string | null | undefined` on the wire (`CategoryResponse` has neither in
 * its `required` list), so a bare `!== null` check would let an absent
 * `undefined` read as present.
 *
 * ## Accessibility
 *
 * Decorative in both forms — the row already renders the category name as
 * text, so an accessible name here would announce it twice. The `<img>`
 * takes `alt=""`; the fallback `<svg>` takes `aria-hidden="true"` with no
 * `role` or `aria-label`.
 */

/** The row/detail glyph size — chosen to read clearly on a 24-unit grid
 *  beside `ListRow`'s 12–16px text, the same relationship `Icon.tsx`'s own
 *  `SIZE` documents for its 24-unit stroke set. */
const SIZE = 20

/** No `color`: the reversal above took the category's own colour off this
 *  component, and leaving the field on the input type would let a caller keep
 *  passing one that silently does nothing. Callers hand this a whole
 *  `Category` — a variable, not a fresh object literal — so TypeScript's
 *  excess-property check does not object to the extra fields it carries. */
export type CategoryIconInput = {
  name: string
  icon?: string | null
  /** The catalogue SVG for `icon`, or null when there is none. Named for the
   *  wire field so a whole `Category` drops in without a mapping step. */
  icon_url?: string | null
}

/** Ruling O's chain, isolated so the render function stays about markup. */
function resolveIconKey(category: CategoryIconInput): string {
  if (category.icon != null) {
    const bySlug = BY_SLUG[category.icon]
    if (bySlug != null) return bySlug
  }
  // The pack is keyed by display name, and backend 270 reworded six of them.
  // Checked BEFORE the exact-name fallback so a renamed category keeps its
  // glyph rather than falling through to the question mark.
  return RENAMED[category.name] ?? category.name
}

export function CategoryIcon({
  category,
  size = SIZE,
}: {
  category: CategoryIconInput
  size?: number
}) {
  const key = resolveIconKey(category)
  // `noUncheckedIndexedAccess` types EVERY access through `PATHS`'s index
  // signature as possibly `undefined` — including `PATHS.Uncategorized`
  // itself, even though the fixture data always carries that key. The `!` is
  // this one, known-present fallback; `PATHS[key]` above it stays unasserted.
  const paths = PATHS[key] ?? PATHS.Uncategorized!

  const style: CSSProperties = { flex: 'none', fill: 'currentColor' }

  return (
    <CatalogueIconImg
      src={category.icon_url}
      size={size}
      fallback={
        <svg
          width={size}
          height={size}
          viewBox={BUNDLED_PACK.viewBox}
          fillRule={BUNDLED_PACK.fillRule}
          aria-hidden="true"
          style={style}
        >
          {paths.map((d, i) => (
            <path key={i} d={d} />
          ))}
        </svg>
      }
    />
  )
}
